import { useNavigate } from 'react-router';

import { IconX } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { MouseEventHandler } from 'react';
import { Trans } from 'react-i18next';

import { Link } from '@cfa/react-components';
import { MessageBlock } from '@cfa/react-core';

import { useAuth } from '@/hooks/useAuth';
import { useFlags } from '@/hooks/useFlags';

import { cn } from '@/utils/tailwind';

import { env } from '@/env';

interface BannerDismissals {
  /** Present after first Smart Search dismiss; may be absent on legacy localStorage. */
  hasSeenSmartSearchAnnouncement?: string[];
}

const bannerDismissalsAtom = atomWithStorage<BannerDismissals>(
  'bannerDismissals',
  { hasSeenSmartSearchAnnouncement: [] },
  undefined,
  { getOnInit: true },
);

const docId =
  env.REACT_APP_ENV === 'prod'
    ? 'crn:pathway:resource:01K9TJ1NCDA1K6XHTHHC7QQS8A'
    : env.REACT_APP_ENV === 'dev'
      ? 'crn:pathway:resource:01JRJZFBYN105S9VE14F41SZT6'
      : 'crn:pathway:resource:01K9TJ1P8M7M417ZKBY0SRYVY8';

// Params match the content-owner link from PATHWAY-4056 / ManagePlansEmbedVideoBanner:
// document header back target is Recent Updates; ResourceView is attributed to that category.
const learnMoreUrl = `/doc/${docId}?${new URLSearchParams({
  from: 'browse',
  backUrl: '/category/recent-updates',
  cat: 'Recent Updates',
  sub: 'Recently Updated Features',
  chooseVersion: 'true',
})}`;

export const SmartSearchAnnouncementBanner: React.FC = () => {
  const { data: auth } = useAuth();
  const { smartsearchAnnouncementBanner } = useFlags();
  const navigate = useNavigate();

  const userId = auth?.userId ?? '';
  const [bannerDismissals, setBannerDismissals] = useAtom(bannerDismissalsAtom);

  const hasSeenSmartSearchAnnouncement = (
    bannerDismissals.hasSeenSmartSearchAnnouncement ?? []
  ).includes(userId);

  const dismiss = () => {
    if (!userId || hasSeenSmartSearchAnnouncement) return;
    setBannerDismissals(prev => ({
      ...prev,
      hasSeenSmartSearchAnnouncement: [
        ...(prev.hasSeenSmartSearchAnnouncement ?? []),
        userId,
      ],
    }));
  };

  const dismissAndNavigateToDoc: MouseEventHandler = event => {
    event.preventDefault();
    dismiss();
    navigate(learnMoreUrl);
  };

  const showBanner =
    smartsearchAnnouncementBanner &&
    !window.native.isNative &&
    !hasSeenSmartSearchAnnouncement;

  if (!showBanner) return null;

  return (
    <div
      className={cn(
        'max-w-content absolute top-16 left-1/2 z-1250 flex w-full -translate-x-1/2 flex-col gap-4 px-4 md:top-8',
        'md:left-[calc(50%+28px)] md:max-w-[min(var(--spacing-content),calc(100%-56px))]',
        'lg:left-[calc(50%+(var(--sidenav-width)/2))] lg:max-w-[min(var(--spacing-content),calc(100%-var(--sidenav-width)))]',
      )}
      data-testid="SmartSearchAnnouncementBanner"
    >
      <MessageBlock.Root severity="info" className="text-start!">
        <div className="flex w-full items-start justify-between gap-2">
          <p className="m-0">
            <Trans
              i18nKey="Browse.smartSearchAnnouncementBannerBody"
              components={{
                smartSearchLink: (
                  <Link
                    href={learnMoreUrl}
                    onClick={dismissAndNavigateToDoc}
                    variant="inline"
                  />
                ),
              }}
            />
          </p>
          <button
            type="button"
            className="flex shrink-0 items-start justify-center p-1"
            aria-label="Dismiss"
            onClick={dismiss}
          >
            <IconX size={20} className="text-grayscale-gray5 cursor-pointer" />
          </button>
        </div>
      </MessageBlock.Root>
    </div>
  );
};
